import type {
  Project,
  ProjectNdaSettings,
  ProjectNdaSignature,
  User,
  UserRole,
} from '../types';

export const STANDARD_PROJECT_NDA_TEMPLATE_VERSION = 'standard-project-nda-v1';

const normalizeIdentity = (value?: string | null): string =>
  (value || '').trim().toLowerCase();

const matchesUserIdentity = (
  signature: Pick<ProjectNdaSignature, 'userId' | 'userEmail'>,
  user: Pick<User, 'id' | 'email'> | null | undefined
): boolean => {
  if (!user) return false;
  const uid = normalizeIdentity(user.id);
  const uemail = normalizeIdentity(user.email);
  const sid = normalizeIdentity(signature.userId);
  const semail = normalizeIdentity(signature.userEmail);
  return Boolean(
    (uid && (uid === sid || uid === semail)) ||
    (uemail && (uemail === semail || uemail === sid))
  );
};

export function getEffectiveProjectNdaSettings(
  project: Project | null | undefined
): ProjectNdaSettings {
  return {
    exemptRoles: project?.ndaSettings?.exemptRoles || [],
    additionalClauses: project?.ndaSettings?.additionalClauses || [],
    standardTemplateVersion:
      project?.ndaSettings?.standardTemplateVersion || STANDARD_PROJECT_NDA_TEMPLATE_VERSION,
    ...(project?.ndaSettings?.updatedAt ? { updatedAt: project.ndaSettings.updatedAt } : {}),
    ...(project?.ndaSettings?.updatedBy ? { updatedBy: project.ndaSettings.updatedBy } : {}),
    ...(project?.ndaSettings?.updatedByName
      ? { updatedByName: project.ndaSettings.updatedByName }
      : {}),
  };
}

export function getStandardProjectNdaClauses(
  organizationName: string,
  projectName: string
): string[] {
  return [
    `You may access confidential project information for "${projectName}" only for authorized work on behalf of ${organizationName}.`,
    `All code, designs, documents, media, datasets, specifications, work products, and any other artifacts created, modified, or delivered for this project belong exclusively to ${organizationName}.`,
    `You may not copy, disclose, distribute, reuse, or retain project artifacts outside approved organizational systems and workflows without written authorization from ${organizationName}.`,
    `Your confidentiality and artifact-handling obligations continue after your project access ends until ${organizationName} releases you from them in writing.`,
  ];
}

export function getProjectNdaTextSections(
  project: Project,
  organizationName: string
): string[] {
  const settings = getEffectiveProjectNdaSettings(project);
  const additional = settings.additionalClauses || [];
  return [
    `This Non-Disclosure Agreement governs your access to the project "${project.name}" for ${organizationName}. By signing, you confirm that you will protect confidential information and that all project artifacts belong to ${organizationName}.`,
    ...getStandardProjectNdaClauses(organizationName, project.name),
    ...(additional.length > 0 ? ['Project-specific additional clauses:', ...additional] : []),
  ];
}

/**
 * Returns true if a user with the given projectMemberRole must sign the NDA.
 * By default ALL roles are required. A role is skipped only when explicitly
 * listed in project.ndaSettings.exemptRoles.
 */
export function isNdaRequiredForRole(
  project: Project | null | undefined,
  projectMemberRole: string | null | undefined
): boolean {
  const settings = getEffectiveProjectNdaSettings(project);
  const exemptRoles = (settings.exemptRoles || []).map((r) => r.toLowerCase());
  if (exemptRoles.length === 0) return true;
  const role = (projectMemberRole || '').trim().toLowerCase();
  return !exemptRoles.includes(role);
}

export function getActiveProjectNdaSignature(
  project: Project | null | undefined,
  user: Pick<User, 'id' | 'email'> | null | undefined
): ProjectNdaSignature | null {
  const signatures = project?.ndaSignatures || [];
  return (
    signatures.find((sig) => !sig.revokedAt && matchesUserIdentity(sig, user)) || null
  );
}

/**
 * Returns true if the user can enter the project without signing.
 * Conditions: role is exempt, OR they already have an active signature.
 */
export function hasAcceptedProjectNda(
  project: Project | null | undefined,
  user: Pick<User, 'id' | 'email'> | null | undefined,
  projectMemberRole: string | null | undefined
): boolean {
  if (!isNdaRequiredForRole(project, projectMemberRole)) return true;
  return !!getActiveProjectNdaSignature(project, user);
}

export function upsertProjectNdaSignature(
  signatures: ProjectNdaSignature[] | undefined,
  nextSignature: ProjectNdaSignature
): ProjectNdaSignature[] {
  const current = signatures || [];
  const filtered = current.filter(
    (sig) =>
      sig.id !== nextSignature.id &&
      !matchesUserIdentity(sig, {
        id: nextSignature.userId,
        email: nextSignature.userEmail,
      })
  );
  return [...filtered, nextSignature];
}

export function revokeProjectNdaSignature(
  signatures: ProjectNdaSignature[] | undefined,
  signatureId: string,
  revokedBy: { id: string; name: string }
): ProjectNdaSignature[] {
  const revokedAt = new Date().toISOString();
  return (signatures || []).map((sig) => {
    if (sig.id !== signatureId || sig.revokedAt) return sig;
    return { ...sig, revokedAt, revokedBy: revokedBy.id, revokedByName: revokedBy.name };
  });
}

/** Convenience: roles that can be made exempt via settings UI */
export const EXEMPTABLE_ROLES: UserRole[] = ['stakeholder'];
